import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";

const fmtDate = (s) => (s ? s.split("-").reverse().join("/") : "—");
const fmtMoney = (v) => `R$ ${Number(String(v).replace(",", ".") || 0).toFixed(2).replace(".", ",")}`;

export function ClientTitlesDialog({ login, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(null);

  useEffect(() => {
    if (!login) return;
    setData(null);
    setLoading(true);
    api
      .get(`/clients/${encodeURIComponent(login)}/titles`)
      .then(({ data }) => {
        setData(data);
        setPhone(data.client?.celular || "");
      })
      .catch((ex) => toast.error(formatApiError(ex)))
      .finally(() => setLoading(false));
  }, [login]);

  const send = async (t) => {
    setSending(t.id);
    try {
      const { data: res } = await api.post(`/clients/${encodeURIComponent(login)}/send-charge`, {
        title_id: String(t.id),
        phone,
      });
      toast.success(`Cobrança do boleto ${t.id} enviada para ${res.phone} (R$ ${res.valor_final})`);
      const { data: fresh } = await api.get(`/clients/${encodeURIComponent(login)}/titles`);
      setData(fresh);
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setSending(null);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <Dialog open={!!login} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="client-titles-dialog" className="bg-slate-950 border-slate-800 text-slate-200 max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-white font-outfit">
            Boletos em aberto {data?.client?.nome ? `— ${data.client.nome}` : ""}
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Envie a cobrança pelo WhatsApp exatamente como o chatbot faz.
          </DialogDescription>
        </DialogHeader>
        {loading && <div className="py-8 text-center text-slate-500">Carregando boletos…</div>}
        {data && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div className="text-xs text-slate-500">
                Login: <span className="font-mono-jb text-emerald-300">{data.client.login}</span>
                <br />
                CPF/CNPJ: <span className="font-mono-jb text-slate-300">{data.client.cpf || "—"}</span>
              </div>
              <div className="md:col-span-2">
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1">Enviar para (WhatsApp)</div>
                <Input
                  data-testid="manual-charge-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="DDD + número (ex: 11999998888)"
                  className="bg-slate-900 border-slate-800 text-white font-mono-jb"
                />
              </div>
            </div>
            <div className="overflow-x-auto border border-slate-800 rounded-md">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800 bg-slate-900/60">
                  <tr>
                    <th className="text-left py-2 px-3">Boleto</th>
                    <th className="text-left py-2 px-3">Vencimento</th>
                    <th className="text-left py-2 px-3">Situação</th>
                    <th className="text-left py-2 px-3">Valor</th>
                    <th className="text-left py-2 px-3">PIX</th>
                    <th className="text-right py-2 px-3"></th>
                  </tr>
                </thead>
                <tbody data-testid="client-titles-tbody">
                  {data.titles.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-6 text-center text-slate-500">
                        Nenhum boleto em aberto para este cliente.
                      </td>
                    </tr>
                  )}
                  {data.titles.map((t) => {
                    const overdue = (t.status || "").toLowerCase() === "vencido" || (t.datavenc && t.datavenc < today);
                    return (
                      <tr key={t.id} data-testid={`title-row-${t.id}`} className="border-b border-slate-800/60">
                        <td className="py-2 px-3 font-mono-jb text-slate-300">
                          {t.id}
                          {t.titulo && <span className="text-slate-600 ml-1 text-xs">({t.titulo})</span>}
                        </td>
                        <td className={`py-2 px-3 font-mono-jb ${overdue ? "text-red-300" : "text-slate-300"}`}>
                          {fmtDate(t.datavenc)}
                        </td>
                        <td className="py-2 px-3" data-testid={`title-status-${t.id}`}>
                          {overdue ? (
                            <Badge className="bg-red-500/10 text-red-300 border border-red-500/30">Vencido</Badge>
                          ) : (
                            <Badge className="bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">Aberto</Badge>
                          )}
                        </td>
                        <td className="py-2 px-3 font-mono-jb text-emerald-300">{fmtMoney(t.valor)}</td>
                        <td className="py-2 px-3">
                          {t.pix_ativo ? (
                            <Badge className="bg-orange-500/10 text-orange-300 border border-orange-500/30">
                              Aguardando R$ {t.pix_valor}
                            </Badge>
                          ) : (
                            <span className="text-slate-600 text-xs">—</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right">
                          <Button
                            size="sm"
                            data-testid={`send-charge-btn-${t.id}`}
                            disabled={sending !== null}
                            onClick={() => send(t)}
                            className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
                          >
                            {sending === t.id ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4 mr-2" />
                            )}
                            Enviar cobrança
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="text-xs text-slate-500 leading-relaxed">
              Exibe apenas boletos <b>em aberto</b> (a vencer) e <b>vencidos</b> — boletos pagos não aparecem. O envio usa o mesmo fluxo do chatbot: valor atualizado via mk-auth (juros/multa), PIX EFI, QR Code,
              copia-e-cola e PDF oficial. Se já existir PIX aguardando para o boleto, ele é reaproveitado.
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
